import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import * as React from "react";
import { getCurrencySymbol } from "utils/currencySymbole";
import { convertToFloat } from "utils/formatNumber";
import ReactCountryFlag from "react-country-flag";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";

interface RowObj { key: string; value: string; }
const columnHelper = createColumnHelper<RowObj>();

function ADPaymentsList(props: {
  tableData: any;
  isLoading: boolean;
  timeFrame: any;
  setTimeFrame: any;
  type: string;
  dates: [Date, Date];
  setDates: any;
}) {
  const { tableData, isLoading, timeFrame, setTimeFrame, type, dates, setDates } = props;
  const isDecline = type === "decline";

  const columns = [
    columnHelper.accessor("key", {
      id: "key",
      header: () => <span className="adp-th">Currency</span>,
      cell: (info: any) => {
        const currency = info.getValue();
        const symbol = getCurrencySymbol(currency?.toUpperCase());
        return (
          <div className="adp-currency-cell" title={symbol?.title}>
            <div className="adp-flag-wrap">
              <ReactCountryFlag svg countryCode={symbol?.country_icon?.toUpperCase()} className="adp-flag" />
            </div>
            <div>
              <span className="adp-currency-code">{currency?.toUpperCase()}</span>
              {symbol?.title && <span className="adp-currency-name">{symbol.title}</span>}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("value", {
      id: "value",
      header: () => <span className="adp-th">Amount</span>,
      cell: (info: any) => (
        <div className="adp-amount-cell">
          <span className="adp-amount">{convertToFloat(info.getValue())}</span>
          <span className="adp-currency-tag">{info?.row?.original?.key?.toUpperCase()}</span>
        </div>
      ),
    }),
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const table = useReactTable({ data, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  const total = tableData?.reduce((acc: number, row: any) => acc + (parseFloat(row.value) || 0), 0);

  return (
    <div className="adp-card">
      {/* Header */}
      <div className="adp-header">
        <div className="adp-header-left">
          <div className={`adp-header-icon${isDecline ? " adp-header-icon--red" : " adp-header-icon--green"}`}>
            {isDecline ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </div>
          <div>
            <h3 className="adp-title">{isDecline ? "Declined" : "Approved"} Payments</h3>
            <p className="adp-subtitle">By currency breakdown</p>
          </div>
        </div>
        {!isLoading && total > 0 && (
          <div className={`adp-total-badge${isDecline ? " adp-total-badge--red" : " adp-total-badge--green"}`}>
            {tableData?.length} {tableData?.length === 1 ? "currency" : "currencies"}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="adp-table-wrap">
        {isLoading ? (
          <div className="adp-loading">
            <div className="adp-spinner" />
            <p className="adp-loading-txt">Loading payments…</p>
          </div>
        ) : (
          <table className="adp-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan} onClick={h.column.getToggleSortingHandler()} className="adp-thead-th">
                      <div className="adp-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="adp-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={12} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={12} /> : <RiArrowUpDownFill size={12} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="adp-table-body">
              {table.getRowModel().rows.map((row, ri) => (
                <tr key={row.id} className={`adp-tbody-tr${ri % 2 === 0 ? "" : " adp-tbody-tr--alt"}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="adp-tbody-td">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={2}>
                    <div className="adp-empty">
                      <div className="adp-empty-icon">
                        {isDecline ? (
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                        ) : (
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                          </svg>
                        )}
                      </div>
                      <p className="adp-empty-title">No {isDecline ? "declined" : "approved"} payments</p>
                      <p className="adp-empty-sub">No data for the selected filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer summary */}
      {!isLoading && tableData?.length > 0 && (
        <div className="adp-footer">
          <span className="adp-footer-label">{tableData.length} {tableData.length === 1 ? "currency" : "currencies"} listed</span>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .adp-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
          display: flex; flex-direction: column;
        }

        /* Header */
        .adp-header {
          padding: 20px 22px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
        }
        .adp-header-left { display: flex; align-items: center; gap: 12px; }
        .adp-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .adp-header-icon--green { background: #f0fdf4; border: 1.5px solid #bbf7d0; color: #16a34a; }
        .adp-header-icon--red   { background: #fff7f7; border: 1.5px solid #fecaca; color: #dc2626; }
        .adp-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height:22px}
        .adp-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .adp-total-badge { font-size: 12px; font-weight: 700; padding: 5px 12px; border-radius: 100px; }
        .adp-total-badge--green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .adp-total-badge--red   { background: #fff7f7; color: #dc2626; border: 1px solid #fecaca; }

        /* Table */
        .adp-table-wrap { overflow-x: auto; flex: 1;max-height: 350px; }
        .adp-table-body { max-height: 350px;overflow-y: auto; padding-right:5px }
        .adp-table-wrap::-webkit-scrollbar { width: 4px; }
        .adp-table-wrap::-webkit-scrollbar-track { background: #f8fafc; }
        .adp-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .adp-table { width: 100%; border-collapse: collapse; }
        .adp-thead-th {
          padding: 11px 18px 9px; background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; cursor: pointer; white-space: nowrap; user-select: none;
        }
        .adp-th-inner { display: flex; align-items: center; gap: 5px; }
        .adp-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .adp-sort-icon { color: #94a3b8; display: flex; align-items: center; }

        .adp-tbody-tr { transition: background 0.12s; }
        .adp-tbody-tr:hover { background: #f8fafc; }
        .adp-tbody-tr--alt { background: #fafbfc; }
        .adp-tbody-tr--alt:hover { background: #f1f5f9; }
        .adp-tbody-td { padding: 13px 18px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .adp-currency-cell { display: flex; align-items: center; gap: 10px; }
        .adp-flag-wrap {
          width: 32px; height: 32px; border-radius: 8px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden;
        }
        .adp-flag { font-size: 18px !important; }
        .adp-currency-code { display: block; font-size: 13px; font-weight: 700; color: #0f172a; }
        .adp-currency-name { display: block; font-size: 11px; color: #94a3b8; font-weight: 500; }
        .adp-amount-cell { display: flex; align-items: center; gap: 6px; }
        .adp-amount { font-size: 14px; font-weight: 700; color: #0f172a; }
        .adp-currency-tag {
          font-size: 11px; font-weight: 600; padding: 2px 7px;
          background: #f1f5f9; color: #64748b; border-radius: 6px;
        }

        /* Footer */
        .adp-footer {
          padding: 12px 18px; border-top: 1px solid #f1f5f9;
          background: #fafbfc;
        }
        .adp-footer-label { font-size: 12px; color: #94a3b8; font-weight: 600; }

        /* States */
        .adp-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 12px; }
        .adp-spinner { width: 26px; height: 26px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: adpSpin 0.7s linear infinite; }
        @keyframes adpSpin { to { transform: rotate(360deg); } }
        .adp-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        .adp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 24px; gap: 10px; }
        .adp-empty-icon { width: 48px; height: 48px; border-radius: 12px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .adp-empty-title { font-size: 13.5px; font-weight: 700; color: #64748b; }
        .adp-empty-sub { font-size: 12px; color: #94a3b8; font-weight: 500; }
      `}</style>
    </div>
  );
}

export default ADPaymentsList;